<template>
  <div class="rich-text-field">
    <div v-if="label" class="rich-text-field__label mb-5">
      {{ label }}
    </div>

    <div v-if="editor" class="rich-text-field__toolbar mb-5">
      <v-btn-toggle multiple dense class="rich-text-field__group">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :input-value="editor.isActive('bold')"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().toggleBold().run()"
            >
              <v-icon>
                mdi-format-bold
              </v-icon>
            </v-btn>
          </template>
          <span>Жирный</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :input-value="editor.isActive('italic')"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().toggleItalic().run()"
            >
              <v-icon>
                mdi-format-italic
              </v-icon>
            </v-btn>
          </template>
          <span>Курсив</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :input-value="editor.isActive('underline')"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().toggleUnderline().run()"
            >
              <v-icon>
                mdi-format-underline
              </v-icon>
            </v-btn>
          </template>
          <span>Подчёркнутый</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :input-value="editor.isActive('strike')"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <v-icon>
                mdi-format-strikethrough
              </v-icon>
            </v-btn>
          </template>
          <span>Зачёркнутый</span>
        </v-tooltip>
      </v-btn-toggle>

      <v-btn-toggle dense class="rich-text-field__group">
        <v-tooltip
          v-for="level in [1, 2, 3]"
          :key="`h${level}`"
          bottom
        >
          <template #activator="{ on, attrs }">
            <v-btn
              style="font-size: 16px;"
              v-bind="attrs"
              :input-value="editor.isActive('heading', { level })"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().toggleHeading({ level }).run()"
            >
              H{{ level }}
            </v-btn>
          </template>
          <span>Заголовок {{ level }}</span>
        </v-tooltip>
      </v-btn-toggle>

      <v-btn-toggle multiple dense class="rich-text-field__group">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :input-value="editor.isActive('bulletList')"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().toggleBulletList().run()"
            >
              <v-icon>
                mdi-format-list-bulleted
              </v-icon>
            </v-btn>
          </template>
          <span>Маркированный список</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :input-value="editor.isActive('orderedList')"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().toggleOrderedList().run()"
            >
              <v-icon>
                mdi-format-list-numbered
              </v-icon>
            </v-btn>
          </template>
          <span>Нумерованный список</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :input-value="editor.isActive('blockquote')"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().toggleBlockquote().run()"
            >
              <v-icon>
                mdi-format-quote-close
              </v-icon>
            </v-btn>
          </template>
          <span>Цитата</span>
        </v-tooltip>
      </v-btn-toggle>

      <v-btn-toggle dense class="rich-text-field__group">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading"
              v-on="on"
              @click="promptLink"
            >
              <v-icon>
                mdi-link-variant
              </v-icon>
            </v-btn>
          </template>
          <span>Ссылка</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading || !editor.isActive('link')"
              v-on="on"
              @click="editor.chain().focus().unsetLink().run()"
            >
              <v-icon>
                mdi-link-variant-off
              </v-icon>
            </v-btn>
          </template>
          <span>Убрать ссылку</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading"
              v-on="on"
              @click="promptImage"
            >
              <v-icon>
                mdi-image-outline
              </v-icon>
            </v-btn>
          </template>
          <span>Изображение</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().setHorizontalRule().run()"
            >
              <v-icon>
                mdi-minus
              </v-icon>
            </v-btn>
          </template>
          <span>Горизонтальная линия</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().setHardBreak().run()"
            >
              <v-icon>
                mdi-keyboard-return
              </v-icon>
            </v-btn>
          </template>
          <span>Перенос строки</span>
        </v-tooltip>
      </v-btn-toggle>

      <v-btn-toggle dense class="rich-text-field__group">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading"
              v-on="on"
              @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()"
            >
              <v-icon>
                mdi-table-plus
              </v-icon>
            </v-btn>
          </template>
          <span>Вставить таблицу</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading || !editor.isActive('table')"
              v-on="on"
              @click="editor.chain().focus().addColumnAfter().run()"
            >
              <v-icon>
                mdi-table-column-plus-after
              </v-icon>
            </v-btn>
          </template>
          <span>Добавить столбец</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading || !editor.isActive('table')"
              v-on="on"
              @click="editor.chain().focus().deleteColumn().run()"
            >
              <v-icon>
                mdi-table-column-remove
              </v-icon>
            </v-btn>
          </template>
          <span>Удалить столбец</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading || !editor.isActive('table')"
              v-on="on"
              @click="editor.chain().focus().addRowAfter().run()"
            >
              <v-icon>
                mdi-table-row-plus-after
              </v-icon>
            </v-btn>
          </template>
          <span>Добавить строку</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading || !editor.isActive('table')"
              v-on="on"
              @click="editor.chain().focus().deleteRow().run()"
            >
              <v-icon>
                mdi-table-row-remove
              </v-icon>
            </v-btn>
          </template>
          <span>Удалить строку</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading || !editor.isActive('table')"
              v-on="on"
              @click="editor.chain().focus().deleteTable().run()"
            >
              <v-icon>
                mdi-table-remove
              </v-icon>
            </v-btn>
          </template>
          <span>Удалить таблицу</span>
        </v-tooltip>
      </v-btn-toggle>

      <v-btn-toggle dense class="rich-text-field__group">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading || !editor.can().undo()"
              v-on="on"
              @click="editor.chain().focus().undo().run()"
            >
              <v-icon>
                mdi-undo
              </v-icon>
            </v-btn>
          </template>
          <span>Отменить</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              :disabled="disabled || loading || !editor.can().redo()"
              v-on="on"
              @click="editor.chain().focus().redo().run()"
            >
              <v-icon>
                mdi-redo
              </v-icon>
            </v-btn>
          </template>
          <span>Повторить</span>
        </v-tooltip>
      </v-btn-toggle>
    </div>

    <editor-content :editor="editor" class="rich-text-field__editor" />
  </div>
</template>

<script>
  import { Editor, EditorContent } from '@tiptap/vue-2'
  import StarterKit from '@tiptap/starter-kit'
  import Link from '@tiptap/extension-link'
  import Image from '@tiptap/extension-image'
  import Underline from '@tiptap/extension-underline'
  import Table from '@tiptap/extension-table'
  import TableRow from '@tiptap/extension-table-row'
  import TableCell from '@tiptap/extension-table-cell'
  import TableHeader from '@tiptap/extension-table-header'

  export default {
    name: 'RichTextField',
    components: { EditorContent },
    props: {
      value: { type: String, default: '' },
      label: { type: String, default: '' },
      disabled: { type: Boolean, default: false },
      loading: { type: Boolean, default: false },
      outlined: { type: Boolean, default: false },
      required: { type: Boolean, default: false },
      rules: { type: Array, default: () => [] },
      createEntity: { type: Boolean, default: false },
      enumClass: { type: Function, default: null },
    },
    data () {
      return { editor: null }
    },
    watch: {
      value (val) {
        if (this.editor && val !== this.editor.getHTML()) {
          this.editor.commands.setContent(val || '', false)
        }
      },
      disabled (val) {
        if (this.editor) this.editor.setEditable(!val)
      },
    },
    mounted () {
      this.editor = new Editor({
        content: this.value || '',
        editable: !this.disabled,
        extensions: [
          StarterKit,
          Underline,
          Link.configure({ openOnClick: false, autolink: true }),
          Image,
          Table.configure({ resizable: true }),
          TableRow,
          TableCell,
          TableHeader,
        ],
        onUpdate: ({ editor }) => {
          this.$emit('input', editor.getHTML())
        },
      })
    },
    beforeDestroy () {
      if (this.editor) this.editor.destroy()
    },
    methods: {
      promptLink () {
        const previous = this.editor.getAttributes('link').href || ''
        const url = window.prompt('URL', previous)
        if (url === null) return
        if (url === '') {
          this.editor.chain().focus().unsetLink().run()
          return
        }
        this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: url })
          .run()
      },
      promptImage () {
        const url = window.prompt('Image URL')
        if (!url) return
        this.editor.chain().focus().setImage({ src: url }).run()
      },
    },
  }
</script>

<style lang="less" scoped>
.rich-text-field {
  color: #000;
  margin-bottom: 8px;
  font-size: 16px;

  &__label {
    color: #999999;
    font-size: 13px;
    margin-bottom: 4px;
  }

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 6px;
  }

  &__group {
    margin-right: 4px;
  }

  &__editor {
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    padding: 8px 12px;
    min-height: 400px;
    max-height: 800px;
    overflow-y: auto;

    ::v-deep .ProseMirror {
      outline: none;
      min-height: 380px;

      &:focus {
        outline: none;
      }

      table {
      border-collapse: collapse;

      th, td {
        padding: 10px;
        border: 1px solid rgb(222, 226, 230);
        font-size: 14px;

        p {
          font-size: 14px;
        }
      }

      th {
        background: #f4f4f4;
        font-weight: bold;
        text-align: left;
      }
    }

      blockquote {
        border-left: 3px solid #ccc;
        padding-left: 12px;
        color: #666;
      }

      hr {
        border: none;
        border-top: 1px solid #ddd;
        margin: 12px 0;
      }

       p {
        //  font-size: 16px;
        //  line-height: 1.3;
        //  margin-bottom: 15px;
        //  display: inline-block;
         margin-bottom: 0;
       }

      ol {
        counter-reset: list-counter;
        padding-left: 1.5em;

        > li {
          &::before {
            counter-increment: list-counter;
            content: counters(list-counter, '.') '. ';
            margin-right: 4px;
          }

          & > p {
            display: inline;
          }
        }
      }

      ol, ul {
      list-style: none;

      & > li {
        margin-bottom: 15px;

        &:before {
          font-weight: normal;
        }

        &:has(> p > strong):before {
          font-weight: bold;
        }
      }

      & ul, ol {
        padding-top: 15px;
        padding-left: 25px;
      }
    }

    ul {
      list-style: disc;

      li:before {
        content: '';
      }
    }
    }
  }
}
</style>
